import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@repo/common/utils/cn";

/**
 *
 * Body Text
 *
 */

export const _BODY_BASE = "font-sans not-italic text-sm";

const textBodyVariants = cva(_BODY_BASE, {
  variants: {
    weight: {
      light: "font-light",
      normal: "font-normal",
      medium: "font-medium",
      bold: "font-bold",
    },
    size: {
      sm: "text-xs",
      md: "text-sm",
      lg: "text-base",
    },
  },
  defaultVariants: {
    weight: "normal",
    size: "md",
  },
});

export type TextBodyProps = React.ComponentProps<"p"> &
  VariantProps<typeof textBodyVariants> & {
    asChild?: boolean;
  };

function TextBody({
  className,
  weight,
  size,
  asChild = false,
  ...props
}: TextBodyProps) {
  const baseClassName = cn(textBodyVariants({ weight, size, className }));

  if (asChild) {
    return (
      <Slot
        data-slot="text-body"
        className={baseClassName}
        {...(props as React.ComponentPropsWithoutRef<typeof Slot>)}
      />
    );
  }

  return <p data-slot="text-body" className={baseClassName} {...props} />;
}

/**
 *
 * Header
 *
 */

const _HEADER_BASE = "font-sans not-italic font-bold leading-normal capitalize";

const textHeadingVariants = cva(_HEADER_BASE, {
  variants: {
    size: {
      sm: "",
      md: "text-xl",
      lg: "text-2xl",
      xl: "text-xl md:text-4xl", // not exact to henry
    },
  },
  defaultVariants: {
    size: "md",
  },
});

export interface TextHeadingProps
  extends React.HTMLAttributes<HTMLParagraphElement>,
    VariantProps<typeof textHeadingVariants> {
  asChild?: boolean;
}

function TextHeading({
  className,
  size,
  asChild = false,
  ...props
}: TextHeadingProps) {
  const baseClassName = cn(textHeadingVariants({ size, className }));

  if (asChild) {
    return (
      <Slot
        data-slot="text-heading"
        className={baseClassName}
        {...(props as React.ComponentPropsWithoutRef<typeof Slot>)}
      />
    );
  }

  return <h2 data-slot="text-heading" className={baseClassName} {...props} />;
}

/**
 *
 * Keyword
 *
 */

const _KEYWORD_BASE = "font-sans not-italic font-bold tracking-wide uppercase";

const textKeywordVariants = cva(_KEYWORD_BASE, {
  variants: {
    size: {
      sm: "text-xs",
      md: "text-sm",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

export interface TextKeywordProps
  extends React.HTMLAttributes<HTMLParagraphElement>,
    VariantProps<typeof textKeywordVariants> {
  asChild?: boolean;
}

function TextKeyword({
  className,
  size,
  asChild = false,
  ...props
}: TextKeywordProps) {
  const baseClassName = cn(textKeywordVariants({ size, className }));

  if (asChild) {
    return (
      <Slot
        data-slot="text-keyword"
        className={baseClassName}
        {...(props as React.ComponentPropsWithoutRef<typeof Slot>)}
      />
    );
  }

  return <span data-slot="text-keyword" className={baseClassName} {...props} />;
}

const Body = TextBody;
const Heading = TextHeading;
const Keyword = TextKeyword;

export {
  Body,
  Heading,
  Keyword,
  textBodyVariants,
  textHeadingVariants,
  textKeywordVariants,
};
